"use client";
// Port Wingo UI (React 19 + Tailwind 4) → Tailwind 3.4, preluat din portul ERP-ului (React 18). Vezi components/wingo/README.md.

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ChangeEventHandler,
  type ComponentProps,
  type CSSProperties,
  type FocusEvent,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  type Ref,
  type RefCallback,
} from "react";
import {
  FIELD_INPUT,
  FieldBox,
  useFieldControl,
  useFormReset,
  type FieldProps,
  type FieldRadius,
  type FieldSize,
  type FieldVariant,
} from "@/components/wingo/ui/field";
import { useControllableState } from "@/components/wingo/hooks/use-controllable-state";
import { matchesShortcut, parseShortcut, useShortcutLabel } from "@/components/wingo/hooks/use-shortcut";
import { cn } from "@/components/wingo/lib/utils";
import { assignRefs, withRef } from "@/components/wingo/lib/with-ref";
import { useDefaults } from "@/components/wingo/lib/ui-config";

export type TextareaSlot =
  | "field"
  | "label"
  | "description"
  | "error"
  | "counter"
  | "box"
  | "textarea"
  | "header"
  | "footer";

// the multiline box without its own padding: the padding moves onto the textarea, so the scrollbar sits at the edge
// and a click anywhere in the box lands in the text
const BOX = "flex-col flex-nowrap items-stretch gap-0 p-0 pointer-coarse:p-0";

// side padding, line height and the vertical padding as a var (min and max heights are rows * 1lh + padding). the
// line heights make one row exactly as tall as an input: 32 / 40 / 48px, and 44px for md on touch. sm reads at 16px
// on phones, so it takes the roomier line there (36px)
const SIZES: Record<FieldSize, string> = {
  sm: "px-2.5 leading-6 [--textarea-py:6px] md:leading-5",
  md: "px-3 leading-6 [--textarea-py:8px] pointer-coarse:[--textarea-py:10px]",
  lg: "px-3.5 leading-6 [--textarea-py:12px]",
};

// rounded-full would turn a tall box into an oval that clips the text in its corners: full is half a one-row height
// instead, so a one-row composer is still a pill and a taller box keeps readable corners
const FULL_RADII: Record<FieldSize, string> = {
  sm: "rounded-[18px] md:rounded-[16px]",
  md: "rounded-[20px] pointer-coarse:rounded-[22px]",
  lg: "rounded-[24px]",
};

// header and footer rows inside the box: a toolbar under the text (attach, send), chips or a quoted reply above it
const FOOTER_SIZES: Record<FieldSize, string> = {
  sm: "gap-1.5 px-1.5 pb-1.5",
  md: "gap-2 px-2 pb-2",
  lg: "gap-2 px-2.5 pb-2.5",
};
const HEADER_SIZES: Record<FieldSize, string> = {
  sm: "gap-1.5 px-1.5 pt-1.5",
  md: "gap-2 px-2 pt-2",
  lg: "gap-2 px-2.5 pt-2.5",
};

// FIELD_INPUT without h-full: the height comes from the rows and the text. a thin scrollbar tinted from the text
// color, and a dark resize grip in dark mode
const TEXTAREA =
  "h-auto w-full flex-auto resize-none py-[var(--textarea-py)] [scrollbar-color:color-mix(in_oklab,currentColor_25%,transparent)_transparent] [scrollbar-width:thin] dark:[color-scheme:dark]";

// rows * line height + the vertical padding; 1lh follows the css line height, so a custom font keeps rows exact
const rowsHeight = (rows: number) => `calc(${rows}lh + var(--textarea-py) * 2)`;

// computed once, in the browser only (effects and handlers)
let fieldSizing: boolean | undefined;
function supportsFieldSizing() {
  if (fieldSizing === undefined) fieldSizing = typeof CSS !== "undefined" && CSS.supports?.("field-sizing", "content") === true;
  return fieldSizing;
}

// React 18: fără cleanup în ref callback; React cheamă din nou ref-ul cu null la detașare
function assignRef<T>(ref: Ref<T> | undefined, node: T | null): void {
  assignRefs(node, ref);
}

function useMergedRef<T>(a: Ref<T> | undefined, b: Ref<T> | undefined): RefCallback<T> {
  return useCallback(
    (node: T | null) => {
      assignRef(a, node);
      assignRef(b, node);
    },
    [a, b],
  );
}

const filled = (node: ReactNode) => node !== undefined && node !== null && node !== false && node !== "";

export type TextareaProps = Omit<
  ComponentProps<"textarea">,
  "size" | "value" | "defaultValue" | "onChange" | "prefix" | "color" | "rows"
> & {
  // controlled text; pair it with onValueChange
  value?: string;
  // starting text while uncontrolled
  defaultValue?: string;
  // the new text on every change, controlled or not
  onValueChange?: (value: string) => void;
  // the native change event, untouched
  onChange?: ChangeEventHandler<HTMLTextAreaElement>;
  // renders the field anatomy around the box
  label?: ReactNode;
  // helper text under the box
  description?: ReactNode;
  // replaces the description, animates in and marks the field invalid
  error?: ReactNode;
  // the invalid look and aria-invalid without a message
  invalid?: boolean;
  // sm 13px, md 14px, lg 15px text (16px on phones); one row is as tall as an input of the same size (sm 36px on phones)
  size?: FieldSize;
  // soft (filled well), outline, underline (a bottom line only) or ghost (inline edit, toolbars)
  variant?: FieldVariant;
  // corner shape, scaled per size like the button's; full caps at half a one-row height; ignored by underline
  radius?: FieldRadius;
  // height when empty
  minRows?: number;
  // grows up to this many rows, then scrolls; 0 = no limit. a value below minRows counts as minRows
  maxRows?: number;
  // grow with the text; false keeps minRows and scrolls
  autosize?: boolean;
  // a vertical resize handle on fine pointers. with autosize a dragged height becomes the new floor and the box still
  // grows past it when the text needs more
  resizable?: boolean;
  // the "count / max" counter under the box; needs maxLength
  showCount?: boolean;
  // the native character limit; 0 = none
  maxLength?: number;
  // a combo in mod+k notation that submits: "mod+enter" by default, "enter" for chat style (shift+enter makes a new
  // line; on touch keyboards a plain enter keeps making new lines), "" turns it off
  submitShortcut?: string;
  // runs on the shortcut with the current text; without it the shortcut calls form.requestSubmit()
  onSubmitShortcut?: (value: string, event: KeyboardEvent<HTMLTextAreaElement>) => void;
  // a row inside the box above the text: attachments, a quoted reply
  header?: ReactNode;
  // a toolbar inside the box under the text: attach, emoji, send
  footer?: ReactNode;
  // anything else for the Field wrapper: optional, labelAction, orientation, labelWidth, hideLabel, counterLabel
  fieldProps?: Partial<FieldProps>;
  classNames?: Partial<Record<TextareaSlot, string>>;
};

function TextareaRender(textareaProps: TextareaProps) {
  const {
    value: valueProp,
    defaultValue = "",
    onValueChange,
    onChange,
    label,
    description,
    error,
    required = false,
    invalid = false,
    disabled = false,
    readOnly = false,
    size = "md",
    variant = "soft",
    radius = "md",
    minRows = 3,
    maxRows = 8,
    autosize = true,
    resizable = false,
    showCount = false,
    maxLength = 0,
    submitShortcut = "mod+enter",
    onSubmitShortcut,
    header,
    footer,
    id,
    fieldProps,
    classNames,
    className,
    style,
    ref,
    onFocus,
    onKeyDown,
    onPointerDown,
    "aria-describedby": describedByProp,
    ...props
  } = useDefaults("textarea", textareaProps);
  const controlled = valueProp !== undefined;
  const [value, setValue] = useControllableState({ value: valueProp, defaultValue, onChange: onValueChange });
  const text = value ?? "";
  const limit = Number.isFinite(maxLength) && maxLength > 0 ? Math.floor(maxLength) : 0;

  const field = useFieldControl({
    id,
    label,
    description,
    error,
    required,
    invalid,
    disabled,
    readOnly,
    size,
    count: text.length,
    maxLength: showCount ? limit : 0,
    fieldProps,
    classNames,
    className,
    style,
  });

  const scale = field.size;
  const underline = variant === "underline";
  const rowsMin = Number.isFinite(minRows) && minRows >= 1 ? Math.floor(minRows) : 1;
  const rowsMax = Number.isFinite(maxRows) && maxRows > 0 ? Math.max(Math.floor(maxRows), rowsMin) : 0;
  const canResize = resizable && !field.disabled && !field.readOnly;

  const innerRef = useRef<HTMLTextAreaElement>(null);
  const setRef = useMergedRef(innerRef, ref as Ref<HTMLTextAreaElement> | undefined);
  // the height the fallback last wrote, to tell it apart from a height the user dragged
  const written = useRef("");
  const [resized, setResized] = useState(false);
  const [inForm, setInForm] = useState(false);

  const shortcut = submitShortcut ? parseShortcut(submitShortcut) : null;
  const shortcutLabel = useShortcutLabel(shortcut ? submitShortcut : null);

  // browsers without field-sizing: measure after every change, one read and one write, before paint
  const measure = useCallback(() => {
    const element = innerRef.current;
    if (!element || !autosize || supportsFieldSizing()) return;
    element.style.height = "auto";
    const next = `${element.scrollHeight}px`;
    element.style.height = next;
    written.current = next;
  }, [autosize]);

  useLayoutEffect(() => {
    measure();
  }, [measure, text, rowsMin, rowsMax, scale]);

  // the fallback also re-measures when the width changes (the text rewraps)
  useEffect(() => {
    const element = innerRef.current;
    if (!element || !autosize || supportsFieldSizing() || typeof ResizeObserver === "undefined") return;
    let width = element.clientWidth;
    let frame = 0;
    const observer = new ResizeObserver(() => {
      if (element.clientWidth === width) return;
      width = element.clientWidth;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [autosize, measure]);

  // the shortcut is only announced when it does something: a handler or a form to submit
  useEffect(() => {
    setInForm(Boolean(innerRef.current?.form));
  }, []);

  useFormReset(innerRef, () => {
    const element = innerRef.current;
    if (!element) return;
    if (!controlled) setValue(element.value);
    element.style.removeProperty("--textarea-floor");
    // a height dragged on a fixed box goes back to minRows too
    if (!autosize) element.style.height = rowsHeight(rowsMin);
    setResized(false);
    measure();
  });

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    onChange?.(event);
    setValue(event.target.value);
  };

  // a form library may have written element.value directly; pick it up when the field is used again
  const handleFocus = (event: FocusEvent<HTMLTextAreaElement>) => {
    onFocus?.(event);
    if (!controlled && event.currentTarget.value !== text) setValue(event.currentTarget.value);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    onKeyDown?.(event);
    // enter confirms an ime composition (japanese, chinese, some android keyboards) instead of submitting
    if (event.defaultPrevented || !shortcut || event.repeat || event.nativeEvent.isComposing || event.key === "Process") return;
    if (!matchesShortcut(event, shortcut)) return;
    const plain = !(shortcut.mod || shortcut.ctrl || shortcut.meta || shortcut.alt);
    // touch keyboards have no shift+enter, so a plain enter keeps making new lines there and the send button submits
    if (plain && window.matchMedia?.("(pointer: coarse)").matches) return;
    const element = event.currentTarget;
    if (onSubmitShortcut) {
      event.preventDefault();
      onSubmitShortcut(element.value, event);
    } else if (element.form) {
      event.preventDefault();
      element.form.requestSubmit();
    }
  };

  // with autosize, a dragged height becomes a floor (a css var react never touches) and the height goes back to the
  // text, so the box still grows once the text needs more
  const handlePointerDown = (event: PointerEvent<HTMLTextAreaElement>) => {
    onPointerDown?.(event);
    if (!canResize || !autosize || event.button !== 0) return;
    const element = event.currentTarget;
    const before = element.style.height;
    const settle = () => {
      window.removeEventListener("pointerup", settle, true);
      window.removeEventListener("mouseup", settle, true);
      requestAnimationFrame(() => {
        const after = element.style.height;
        if (!after || after === before || after === written.current) return;
        element.style.setProperty("--textarea-floor", after);
        element.style.height = "";
        measure();
        setResized(true);
      });
    };
    window.addEventListener("pointerup", settle, true);
    window.addEventListener("mouseup", settle, true);
  };

  const textareaStyle: CSSProperties = {
    minHeight: `max(${rowsHeight(rowsMin)}, var(--textarea-floor, 0px))`,
    maxHeight: rowsMax ? rowsHeight(rowsMax) : undefined,
    height: autosize ? undefined : rowsHeight(rowsMin),
  };

  const describedBy = [field.describedBy, describedByProp].filter(Boolean).join(" ") || undefined;

  const box = (
    <FieldBox
      data-slot="textarea-box"
      data-autosize={autosize || undefined}
      data-resized={resized || undefined}
      variant={variant}
      size={scale}
      radius={radius}
      multiline
      invalid={field.invalid}
      disabled={field.disabled}
      readOnly={field.readOnly}
      className={cn(BOX, radius === "full" && !underline && FULL_RADII[scale], !field.isWrapped && className, classNames?.box)}
      style={field.isWrapped ? undefined : style}
    >
      {filled(header) && (
        <div
          data-slot="textarea-header"
          className={cn("flex min-w-0 flex-wrap items-center", HEADER_SIZES[scale], underline && "px-0", classNames?.header)}
        >
          {header}
        </div>
      )}
      <textarea
        data-slot="textarea"
        rows={rowsMin}
        inputMode="text"
        enterKeyHint="enter"
        aria-keyshortcuts={shortcut && (onSubmitShortcut || inForm) ? shortcutLabel.aria : undefined}
        {...props}
        {...field.controlProps}
        aria-describedby={describedBy}
        ref={setRef}
        maxLength={limit || undefined}
        {...(controlled ? { value: text } : { defaultValue })}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onPointerDown={handlePointerDown}
        style={textareaStyle}
        className={cn(
          FIELD_INPUT,
          TEXTAREA,
          SIZES[scale],
          autosize && "field-sizing-content",
          // the scrollbar clips to the box corners; a resize grip needs its square corner
          canResize ? "pointer-fine:resize-y" : "rounded-[inherit]",
          underline && "px-0",
          classNames?.textarea,
        )}
      />
      {filled(footer) && (
        <div
          data-slot="textarea-footer"
          className={cn("flex min-w-0 items-center", FOOTER_SIZES[scale], underline && "px-0", classNames?.footer)}
        >
          {footer}
        </div>
      )}
    </FieldBox>
  );

  return field.wrap(box);
}

// React 18: ref-urile ajung la randare doar prin forwardRef (vezi lib/with-ref.ts)
export const Textarea = withRef(TextareaRender, "Textarea");
